import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import * as XLSX from 'xlsx';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import * as api from '../api/drive';
import DriveShareDialog from '../components/DriveShareDialog';

// Trình soạn bảng tính nhúng Fortune-sheet (~5MB) -> tải LƯỜI: chỉ nạp khi mở 1 sheet, khỏi nặng bundle chính.
const DriveEditor = lazy(() => import('./DriveEditor'));

// TẠM TẮT "Quét đơn PMBox -> bảng tính" theo yêu cầu GĐ (2026-10-16), chờ yêu cầu chi tiết.
// Bật lại: đổi thành true (backend cũng có cờ PMBOX_SCAN_ENABLED trong driveController.js).
const PMBOX_SCAN_ENABLED = false;

const SCOPES = [
  { key: 'mine', label: 'Của tôi', ico: 'ti-user' },
  { key: 'shared', label: 'Được chia sẻ', ico: 'ti-users' },
  { key: 'recent', label: 'Gần đây', ico: 'ti-clock' },
  { key: 'starred', label: 'Đánh dấu sao', ico: 'ti-star' },
  { key: 'trash', label: 'Thùng rác', ico: 'ti-trash' },
];

// Hôm nay -> giờ; hôm qua/hôm kia; cũ hơn -> dd/mm/yyyy (giống màn chat).
function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(new Date()) - startOf(dt)) / 86400000);
  if (diff <= 0) return dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  if (diff === 1) return 'Hôm qua';
  if (diff === 2) return 'Hôm kia';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
}
function fmtSize(n) {
  if (!n) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}

export default function DrivePage() {
  const toast = useToast();
  const { me } = useAuth();
  const admin = !!(me && (me.role === 'GD' || me.role === 'CV')); // GĐ/CV thấy + chia sẻ MỌI file
  const isDT = me?.role === 'DT'; // ĐỐI TÁC: CHỈ xem file được chia sẻ (không sở hữu/không tạo file riêng)
  const scopeList = isDT
    ? SCOPES.filter((s) => s.key === 'shared') // đối tác chỉ có mục "Được chia sẻ"
    : (admin ? [{ key: 'all', label: 'Tất cả file', ico: 'ti-stack-2' }, ...SCOPES] : SCOPES);
  const [scope, setScope] = useState(isDT ? 'shared' : 'mine');
  const [parent, setParent] = useState(null); // thư mục đang mở (chỉ scope 'mine')
  const [crumbs, setCrumbs] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // {id, name, myRole}
  const [shareFor, setShareFor] = useState(null);
  const [menuFor, setMenuFor] = useState(null); // id dòng đang mở menu ⋯
  const [creating, setCreating] = useState(false); // dropdown "Tạo mới"
  const [scanOpen, setScanOpen] = useState(false); // hộp "Quét đơn PMBox"
  const [dialog, setDialog] = useState(null); // modal nhập tên / xác nhận (thay window.prompt/confirm — hợp app Capacitor)
  const createRef = useRef(null);
  const fileInputRef = useRef(null);

  // Tải .xlsx/.csv lên -> parse bằng SheetJS -> chuyển sang celldata Fortune-sheet -> tạo bảng tính rồi mở.
  const importXlsx = async (file) => {
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellFormula: true });
      const sheets = wb.SheetNames.map((nm, i) => {
        const ws = wb.Sheets[nm];
        const celldata = [];
        if (ws && ws['!ref']) {
          const range = XLSX.utils.decode_range(ws['!ref']);
          for (let r = range.s.r; r <= range.e.r; r += 1) {
            for (let c = range.s.c; c <= range.e.c; c += 1) {
              const cell = ws[XLSX.utils.encode_cell({ r, c })];
              if (!cell) continue;
              let cv = cell.v != null ? cell.v : '';
              let cm = cell.w != null ? String(cell.w) : (cell.v != null ? String(cell.v) : '');
              // SỐ NGUYÊN LỚN mà Excel hiện KHOA HỌC (mã tracking / ID dài 12–22 chữ số) -> giữ dạng TEXT ĐỦ
              // CHỮ SỐ (vd "3.84023E+11" -> "384023123456"), tránh ký hiệu khoa học. Không đụng ô công thức
              // hay số lẻ. (Số >15 chữ số vốn đã mất chính xác từ file nguồn nếu nguồn lưu dạng SỐ.)
              if (!cell.f && cell.t === 'n' && Number.isInteger(cell.v) && /e/i.test(String(cell.w || ''))) {
                try { const full = BigInt(cell.v).toString(); cv = full; cm = full; } catch (e) { /* giữ nguyên nếu không ép được */ }
              }
              const v = { v: cv, m: cm };
              // GIỮ CÔNG THỨC (SheetJS lưu không có dấu '='). BỎ tiền tố _xlfn. / _xlfn._xlws. mà SheetJS
              // gắn cho hàm mới (CONCAT, IFS, TEXTJOIN, XLOOKUP, FILTER…): Fortune-sheet KHÔNG hiểu tiền tố
              // -> ô hiện giá trị cache lúc mở nhưng KÉO/tính lại thì ra #ERROR!. Bỏ tiền tố là tính được.
              if (cell.f) v.f = '=' + String(cell.f).replace(/_xlfn\._xlws\./g, '').replace(/_xlfn\./g, '');
              celldata.push({ r, c, v });
            }
          }
        }
        return { name: String(nm || `Trang ${i + 1}`).slice(0, 31), celldata, order: i };
      });
      const name = file.name.replace(/\.(xlsx|xls|csv)$/i, '');
      const f = await api.createDrive({ kind: 'sheet', name, data: sheets.length ? sheets : null, parent: scope === 'mine' ? parent : null });
      toast('Đã tải lên: ' + name);
      setEditing(f);
    } catch (e) {
      toast(e?.response?.data?.message || 'Không đọc được file Excel', 'danger');
    }
  };

  // Quét đơn PMBox theo khoảng ngày -> backend tạo bảng tính mới -> mở luôn. Lỗi ném lên để hộp giữ nguyên.
  const runPmboxScan = async ({ from, to }) => {
    const f = await api.scanPmboxOrders({ from, to });
    toast(`Đã quét ${f.count || 0} đơn PMBox vào bảng tính`);
    setScanOpen(false);
    setEditing(f);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listDrive(scope, scope === 'mine' ? parent : null);
      setFiles(res.files || []);
      setCrumbs(scope === 'mine' && parent ? await api.driveBreadcrumb(parent) : []);
    } catch (e) {
      toast('Không tải được kho file', 'danger');
    } finally {
      setLoading(false);
    }
  }, [scope, parent, toast]);
  useEffect(() => { load(); }, [load]);

  // Đóng dropdown/menu khi bấm ra ngoài.
  useEffect(() => {
    const off = () => { setMenuFor(null); setCreating(false); };
    window.addEventListener('click', off);
    return () => window.removeEventListener('click', off);
  }, []);

  const goScope = (s) => { setScope(s); setParent(null); setQ(''); };
  const openFolder = (f) => { setScope('mine'); setParent(f.id); };

  const create = (kind) => {
    setCreating(false);
    setDialog({
      type: 'name', title: kind === 'folder' ? 'Tạo thư mục' : 'Tạo bảng tính', okText: 'Tạo',
      value: kind === 'folder' ? 'Thư mục mới' : 'Bảng tính mới',
      onOk: async (name) => {
        try {
          const f = await api.createDrive({ kind, name: name.trim(), parent: scope === 'mine' ? parent : null });
          toast(kind === 'folder' ? 'Đã tạo thư mục' : 'Đã tạo bảng tính');
          if (kind === 'sheet') setEditing(f); else load();
        } catch (e) { toast(e?.response?.data?.message || 'Không tạo được', 'danger'); }
      },
    });
  };

  const openItem = (f) => {
    if (f.kind === 'folder') openFolder(f);
    else setEditing(f);
  };

  const rename = (f) => setDialog({
    type: 'name', title: 'Đổi tên', okText: 'Lưu', value: f.name,
    onOk: async (name) => { try { await api.updateDrive(f.id, { name: name.trim() }); load(); } catch (e) { toast(e?.response?.data?.message || 'Không đổi được tên', 'danger'); } },
  });
  const star = async (f) => { try { await api.starDrive(f.id); load(); } catch (e) { toast('Lỗi', 'danger'); } };
  const trash = (f) => setDialog({
    type: 'confirm', title: 'Chuyển vào thùng rác', okText: 'Xoá', danger: true,
    message: `Chuyển "${f.name}" vào thùng rác?`,
    onOk: async () => { try { await api.trashDrive(f.id); toast('Đã chuyển vào thùng rác'); load(); } catch (e) { toast(e?.response?.data?.message || 'Không xoá được', 'danger'); } },
  });
  const restore = async (f) => { try { await api.restoreDrive(f.id); toast('Đã khôi phục'); load(); } catch (e) { toast('Lỗi', 'danger'); } };
  const purge = (f) => setDialog({
    type: 'confirm', title: 'Xoá vĩnh viễn', okText: 'Xoá vĩnh viễn', danger: true,
    message: `Xoá VĨNH VIỄN "${f.name}"? Không khôi phục được.`,
    onOk: async () => { try { await api.purgeDrive(f.id); toast('Đã xoá vĩnh viễn'); load(); } catch (e) { toast('Lỗi', 'danger'); } },
  });

  const shown = files.filter((f) => !q || f.name.toLowerCase().includes(q.toLowerCase()));

  if (editing) {
    return (
      <>
        <Suspense fallback={<div className="empty" style={{ padding: 40 }}>Đang mở trình soạn bảng tính…</div>}>
          <DriveEditor file={editing} isAdmin={admin} onClose={() => { setEditing(null); load(); }} onShare={(f) => setShareFor(f)} />
        </Suspense>
        {/* Hộp Chia sẻ PHẢI render ở đây nữa — nhánh editing return sớm, nếu chỉ để ở nhánh danh sách thì nút Chia sẻ trong editor bấm KHÔNG hiện gì. */}
        {shareFor && <DriveShareDialog file={shareFor} onClose={() => { setShareFor(null); load(); }} />}
      </>
    );
  }

  const roleBadge = (f) => {
    if (f.isOwner) return null;
    const edit = f.myRole === 'edit' || f.myRole === 'owner';
    return <span className="tag" style={{ background: edit ? 'color-mix(in srgb, var(--ok) 15%, transparent)' : 'color-mix(in srgb, var(--accent) 15%, transparent)', color: edit ? 'var(--ok)' : 'var(--accent)' }}>{edit ? 'Sửa' : 'Xem'}</span>;
  };

  return (
    <div className="page">
      <div className="page-head"><h2><i className="ti ti-folders" /> Kho file</h2></div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Rail scope */}
        <div className="card" style={{ width: 180, flex: 'none', padding: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* ĐỐI TÁC chỉ XEM file được chia sẻ -> ẩn tạo mới / tải lên (họ không sở hữu file riêng). */}
          {!isDT && (<>
          <div style={{ position: 'relative' }} ref={createRef}>
            <button className="btn" style={{ width: '100%', justifyContent: 'center' }} onClick={(e) => { e.stopPropagation(); setCreating((v) => !v); }}>
              <i className="ti ti-plus" /> Tạo mới
            </button>
            {creating && (
              <div className="card" style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 20, padding: 4 }} onClick={(e) => e.stopPropagation()}>
                <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => create('folder')}><i className="ti ti-folder-plus" /> Thư mục</button>
                <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => create('sheet')}><i className="ti ti-table-plus" /> Bảng tính</button>
              </div>
            )}
          </div>
          <button className="btn ghost sm" style={{ justifyContent: 'flex-start', marginTop: 2 }} onClick={() => fileInputRef.current?.click()}>
            <i className="ti ti-upload" /> Tải .xlsx lên
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) importXlsx(f); e.target.value = ''; }} />
          </>)}
          {PMBOX_SCAN_ENABLED && me?.role !== 'DT' && (
            <button className="btn ghost sm" style={{ justifyContent: 'flex-start' }} onClick={() => setScanOpen(true)}>
              <i className="ti ti-download" /> Quét đơn PMBox
            </button>
          )}
          <div style={{ height: 1, background: 'var(--border)', margin: '6px 4px' }} />
          {scopeList.map((s) => (
            <button key={s.key} className="btn ghost sm" onClick={() => goScope(s.key)}
              style={{ justifyContent: 'flex-start', background: scope === s.key ? 'color-mix(in srgb, var(--accent) 13%, transparent)' : 'transparent', color: scope === s.key ? 'var(--accent)' : 'var(--text2)', fontWeight: scope === s.key ? 600 : 400 }}>
              <i className={'ti ' + s.ico} /> {s.label}
            </button>
          ))}
        </div>

        {/* Nội dung */}
        <div className="card" style={{ flex: 1, minWidth: 0, padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
            {scope === 'mine' ? (
              <div style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <span style={{ cursor: 'pointer', color: parent ? 'var(--accent)' : 'inherit' }} onClick={() => setParent(null)}>Kho file</span>
                {crumbs.map((c) => (<span key={c.id}><span style={{ color: 'var(--text3)' }}> › </span><span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => setParent(c.id)}>{c.name}</span></span>))}
              </div>
            ) : (<div style={{ fontSize: 14, fontWeight: 500 }}>{scopeList.find((s) => s.key === scope)?.label}</div>)}
            <div style={{ flex: 1 }} />
            <div className="row" style={{ gap: 6, background: 'var(--panel2, var(--panel))', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 8px' }}>
              <i className="ti ti-search" style={{ color: 'var(--text3)' }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm file…" style={{ border: 'none', background: 'none', outline: 'none', fontSize: 13, width: 140 }} />
            </div>
          </div>

          {loading ? (
            <div className="empty" style={{ padding: 30 }}>Đang tải…</div>
          ) : shown.length === 0 ? (
            <div className="empty" style={{ padding: 30 }}>{scope === 'trash' ? 'Thùng rác trống.' : scope === 'shared' ? 'Chưa có file nào được chia sẻ với bạn.' : isDT ? 'Chưa có file nào được chia sẻ với bạn.' : 'Chưa có file. Bấm "Tạo mới".'}</div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 90px 110px 36px', gap: 8, padding: '8px 14px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase' }}>
                <span>Tên</span><span>Chủ sở hữu</span><span>Chia sẻ</span><span>Sửa lần cuối</span><span></span>
              </div>
              {shown.map((f) => (
                <div key={f.id} style={{ display: 'grid', gridTemplateColumns: '1fr 130px 90px 110px 36px', gap: 8, padding: '9px 14px', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, minWidth: 0, cursor: 'pointer' }} onClick={() => openItem(f)}>
                    <i className={'ti ' + (f.kind === 'folder' ? 'ti-folder' : 'ti-table')} style={{ fontSize: 18, color: f.kind === 'folder' ? 'var(--warn)' : 'var(--ok)' }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    {f.starred && <i className="ti ti-star" style={{ fontSize: 13, color: 'var(--warn)' }} />}
                    {f.locked && !f.locked.mine && <span title={'Đang sửa bởi ' + f.locked.byName}><i className="ti ti-lock" style={{ fontSize: 13, color: 'var(--danger)' }} /></span>}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.isOwner ? 'Tôi' : f.ownerName}</span>
                  <span style={{ fontSize: 11 }}>{roleBadge(f) || (f.shareCount > 0 ? <span className="tag" style={{ background: 'color-mix(in srgb, var(--accent) 13%, transparent)', color: 'var(--accent)' }}>{f.shareCount}</span> : <span style={{ color: 'var(--text3)' }}>Riêng tư</span>)}</span>
                  <span style={{ fontSize: 12, color: 'var(--text2)' }}>{fmtDate(f.updatedAt)}</span>
                  <span style={{ position: 'relative', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <button className="btn ghost sm" style={{ padding: 4 }} onClick={() => setMenuFor(menuFor === f.id ? null : f.id)}><i className="ti ti-dots-vertical" /></button>
                    {menuFor === f.id && (
                      <div className="card" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 30, padding: 4, minWidth: 160, textAlign: 'left' }}>
                        {scope === 'trash' ? (
                          <>
                            <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => { setMenuFor(null); restore(f); }}><i className="ti ti-restore" /> Khôi phục</button>
                            {f.isOwner && <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start', color: 'var(--danger)' }} onClick={() => { setMenuFor(null); purge(f); }}><i className="ti ti-trash-x" /> Xoá vĩnh viễn</button>}
                          </>
                        ) : (
                          <>
                            <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => { setMenuFor(null); openItem(f); }}><i className="ti ti-folder-open" /> Mở</button>
                            {(f.isOwner || admin) && <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => { setMenuFor(null); setShareFor(f); }}><i className="ti ti-share" /> Chia sẻ</button>}
                            {(f.myRole === 'owner' || f.myRole === 'edit') && <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => { setMenuFor(null); rename(f); }}><i className="ti ti-edit" /> Đổi tên</button>}
                            <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => { setMenuFor(null); star(f); }}><i className="ti ti-star" /> {f.starred ? 'Bỏ sao' : 'Đánh dấu sao'}</button>
                            {f.isOwner && <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start', color: 'var(--danger)' }} onClick={() => { setMenuFor(null); trash(f); }}><i className="ti ti-trash" /> Xoá</button>}
                          </>
                        )}
                      </div>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {shareFor && <DriveShareDialog file={shareFor} onClose={() => { setShareFor(null); load(); }} />}
      {dialog && <DriveDialog key={dialog.title + (dialog.value || '')} dialog={dialog} onClose={() => setDialog(null)} />}
      {PMBOX_SCAN_ENABLED && scanOpen && <PmboxScanDialog onScan={runPmboxScan} onClose={() => setScanOpen(false)} />}
    </div>
  );
}

// Hộp "Quét đơn PMBox": chọn khoảng ngày -> gọi backend lấy đơn PMBox đổ vào bảng tính mới.
function PmboxScanDialog({ onScan, onClose }) {
  const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
  const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(todayStr);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async () => {
    if (busy) return;
    if (!from || !to) { setErr('Chọn khoảng ngày'); return; }
    if (from > to) { setErr('Từ ngày phải trước đến ngày'); return; }
    setErr(''); setBusy(true);
    try { await onScan({ from, to }); }
    catch (e) { setErr(e?.response?.data?.message || 'Không quét được đơn PMBox'); setBusy(false); }
  };
  const inp = { padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: 'var(--panel)', color: 'var(--text)' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !busy && onClose()}>
      <div className="card" style={{ width: 400, maxWidth: '92vw', padding: 18 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}><i className="ti ti-download" /> Quét đơn PMBox</h3>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>Lấy đơn hàng PMBox theo khoảng ngày và đổ vào một bảng tính mới (của bạn).</div>
        <div className="row" style={{ gap: 10 }}>
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text2)' }}>
            Từ ngày
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} disabled={busy} style={inp} />
          </label>
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text2)' }}>
            Đến ngày
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} disabled={busy} style={inp} />
          </label>
        </div>
        {err && <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 10 }}>{err}</div>}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn ghost" onClick={onClose} disabled={busy}>Huỷ</button>
          <button className="btn" onClick={run} disabled={busy}>
            {busy ? 'Đang quét…' : <><i className="ti ti-download" /> Quét</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal nhập tên / xác nhận — thay window.prompt/confirm (vốn bị chặn trong WebView Capacitor + xấu).
function DriveDialog({ dialog, onClose }) {
  const [val, setVal] = useState(dialog.value || '');
  const inputRef = useRef(null);
  useEffect(() => { if (dialog.type === 'name' && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [dialog.type]);
  const ok = () => {
    if (dialog.type === 'name' && !val.trim()) return;
    onClose();
    dialog.onOk(dialog.type === 'name' ? val.trim() : true);
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div className="card" style={{ width: 380, maxWidth: '92vw', padding: 18 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{dialog.title}</h3>
        {dialog.type === 'name' ? (
          <input ref={inputRef} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') ok(); }}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
        ) : (
          <div style={{ fontSize: 14, color: 'var(--text2)' }}>{dialog.message}</div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn ghost" onClick={onClose}>Huỷ</button>
          <button className="btn" style={dialog.danger ? { background: 'var(--danger)', borderColor: 'var(--danger)' } : undefined} onClick={ok}>{dialog.okText || 'OK'}</button>
        </div>
      </div>
    </div>
  );
}
